import { useEffect } from 'react'

import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { Button, Label, RadioGroup, RadioGroupItem } from '@/components/ui'
import { transformarPJParaVinculoRepresentanteLegal } from '@/features/proposta/entities/Comprador/form-to-listagem/mapper-comprador-listagem-para-formulario'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/layouts'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import type {
  CompradorVinculoStepSchemaInput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { Cnpj } from '@/utils/cnpj'

type TVincularCompradorRepresentanteLegal = {
  clientesJuridicos: TPropostaSchemaInput['cadastroCompradores']['clientesJuridicos']
  onBack: () => void
  onNext: () => void
}

export const VincularCompradorAoRepresentanteLegal = ({
  clientesJuridicos,
  onBack,
  onNext,
}: TVincularCompradorRepresentanteLegal) => {
  const { trigger, reset, watch } = useFormContext<
    CompradorVinculoStepSchemaInput,
    unknown,
    CompradorVinculoStepSchemaOutput
  >()
  const compradorJuridicoId = watch('compradorVinculadoIds.0') ?? ''
  const options = clientesJuridicos?.toSorted((a, b) => a.razaoSocial.localeCompare(b.razaoSocial))

  const handleValidateOnNext = async () => {
    const isValid = await trigger('compradorVinculadoIds')
    if (isValid) {
      onNext()
    }
  }

  const handlePreencherPjSeApenasUm = () => {
    if (options?.length === 1 && options[0]) {
      const unicoPj = options[0]
      reset(transformarPJParaVinculoRepresentanteLegal(unicoPj))
    }
  }

  useEffect(() => {
    handlePreencherPjSeApenasUm()
  }, [])

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>
          {t('proposta:cadastro_compradores.steps.titulo_vinculo_representante_legal')}
        </StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="grid pt-3">
          <RadioGroup
            className="flex flex-col gap-6"
            value={compradorJuridicoId}
            onValueChange={(v) => {
              const pessoaJuridica = options?.find((option) => option.id === v)
              if (pessoaJuridica) {
                reset(transformarPJParaVinculoRepresentanteLegal(pessoaJuridica))
              }
            }}
          >
            {options?.map((option) => (
              <div key={option.cnpj} className="flex items-center gap-2">
                <RadioGroupItem value={option.id} id={option.cnpj} />
                <Label className="font-nunito grid font-semibold" htmlFor={option.cnpj}>
                  {option.razaoSocial}
                  <span className="font-normal">CNPJ: {new Cnpj(option.cnpj).format()}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={onBack}>
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-27" onClick={handleValidateOnNext}>
          {t('proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
